import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { money, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, StatCard } from "@/components/ui-bits";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Loader2, Trophy, Target, Copy, Crown, Medal, Check } from "lucide-react";

export default function Targets() {
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [data, setData] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [saving, setSaving] = useState(null);

  const load = useCallback(() => api.get(`/admin/targets?month=${month}`).then((r) => { setData(r.data); setDrafts({}); }), [month]);
  useEffect(() => { setData(null); load(); }, [load]);

  const draft = (r) => drafts[r.agent_id] ?? { bags_target: r.bags_target ?? "", sales_target: r.sales_target ?? "" };
  const setDraft = (r, patch) => setDrafts({ ...drafts, [r.agent_id]: { ...draft(r), ...patch } });

  const save = async (r) => {
    const d = draft(r);
    setSaving(r.agent_id);
    try {
      await api.put(`/admin/targets/${r.agent_id}?month=${month}`, {
        bags_target: d.bags_target === "" ? null : Number(d.bags_target),
        sales_target: d.sales_target === "" ? null : Number(d.sales_target),
      });
      toast.success(`Target saved for ${r.name}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(null);
    }
  };

  const copyPrev = async () => {
    try {
      const { data: d } = await api.post("/admin/targets/copy", { month });
      toast.success(d.copied ? `Copied ${d.copied} target(s) from ${d.from}` : `No targets to copy from ${d.from}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div>
      <PageHeader
        title="Targets & Leaderboard"
        subtitle="Monthly bag and collected-sales targets per agent. Achievement follows cleared payments - the same basis as commission."
        actions={
          <div className="flex items-center gap-2">
            <Input type="month" className="w-40 h-10" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="targets-month-input" />
            <Button variant="outline" onClick={copyPrev} data-testid="copy-targets-button"><Copy className="w-4 h-4 mr-1" /> Copy last month</Button>
          </div>
        }
      />
      {!data ? <div className="flex justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div> : (
        <div className="space-y-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard label="Team collected sales" value={money(data.summary.collected_sales)} sub={data.summary.sales_target ? `${data.summary.sales_pct}% of ${money(data.summary.sales_target)}` : "no sales target set"} icon={Target} />
            <StatCard label="Team bags" value={data.summary.bags_sold} sub={data.summary.bags_target ? `${data.summary.bags_pct}% of ${data.summary.bags_target} bags` : "no bag target set"} icon={Trophy} />
            <StatCard label="Agents on target" value={`${data.summary.on_track}/${data.summary.with_target}`} sub={`${data.summary.agents} agents · ${data.scope === "team" ? "my team" : "company"}`} accent />
            <StatCard label="Month elapsed" value={`${data.summary.month_progress_pct}%`} sub="pace guide - achievement should track this" />
          </div>

          <Card className="p-0 overflow-hidden" data-testid="leaderboard-card">
            <div className="px-5 py-4 border-b border-border flex items-center gap-2"><Trophy className="w-4 h-4 text-primary" /><h3 className="font-display font-semibold">Leaderboard · {month}</h3><span className="text-xs text-muted-foreground ml-1">ranked by sales-target achievement, then collected sales</span></div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                    <th className="text-left font-medium px-4 py-3">#</th>
                    <th className="text-left font-medium px-4 py-3">Agent</th>
                    <th className="text-right font-medium px-4 py-3">Collected</th>
                    <th className="text-left font-medium px-4 py-3 min-w-[220px]">Sales target (AED)</th>
                    <th className="text-right font-medium px-4 py-3">Bags</th>
                    <th className="text-left font-medium px-4 py-3 min-w-[200px]">Bag target</th>
                    <th className="text-right font-medium px-4 py-3">New cust.</th>
                    <th className="text-right font-medium px-4 py-3">Earnings</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((r) => {
                    const d = draft(r);
                    const dirty = String(d.bags_target) !== String(r.bags_target ?? "") || String(d.sales_target) !== String(r.sales_target ?? "");
                    return (
                      <tr key={r.agent_id} className="border-b border-border/50 hover:bg-secondary/40" data-testid={`target-row-${r.agent_id}`}>
                        <td className="px-4 py-3 font-display font-bold">
                          {r.rank === 1 ? <Crown className="w-5 h-5 text-amber-500" /> : r.rank <= 3 ? <Medal className="w-5 h-5 text-muted-foreground" /> : r.rank}
                        </td>
                        <td className="px-4 py-3">
                          <Link to={`/dashboard/agents/${r.agent_id}`} className="font-medium text-primary hover:underline">{r.name}</Link>
                          <div className="text-xs text-muted-foreground">{r.agent_code}{r.is_star ? " · Star agent" : ""}{r.status !== "active" ? ` · ${r.status}` : ""}</div>
                        </td>
                        <td className="px-4 py-3 text-right font-medium">{money(r.collected_sales)}<div className="text-[11px] text-muted-foreground">invoiced {money(r.invoiced_sales)}</div></td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <Input type="number" className="h-8 w-28" value={d.sales_target} placeholder="—" onChange={(e) => setDraft(r, { sales_target: e.target.value })} data-testid={`sales-target-input-${r.agent_id}`} />
                            <Ach pct={r.sales_pct} gap={r.sales_gap != null ? money(r.sales_gap) : null} testid={`sales-pct-${r.agent_id}`} />
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right">{r.bags_sold}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <Input type="number" className="h-8 w-24" value={d.bags_target} placeholder="—" onChange={(e) => setDraft(r, { bags_target: e.target.value })} data-testid={`bags-target-input-${r.agent_id}`} />
                            <Ach pct={r.bags_pct} gap={r.bags_gap != null ? `${r.bags_gap} bags` : null} testid={`bags-pct-${r.agent_id}`} />
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right">{r.new_customers}</td>
                        <td className="px-4 py-3 text-right text-primary">{money(r.total_earnings)}</td>
                        <td className="px-4 py-3 text-right">
                          <Button size="sm" variant={dirty ? "default" : "ghost"} disabled={!dirty || saving === r.agent_id} onClick={() => save(r)} data-testid={`save-target-${r.agent_id}`}>
                            {saving === r.agent_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-3.5 h-3.5 mr-1" />Save</>}
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}

function Ach({ pct, gap, testid }) {
  if (pct == null) return <span className="text-xs text-muted-foreground">no target</span>;
  const tone = pct >= 100 ? "bg-primary" : pct >= 70 ? "bg-amber-500" : "bg-destructive";
  return (
    <div className="flex-1 min-w-[90px]" data-testid={testid}>
      <div className="flex items-center justify-between text-xs"><span className="font-medium">{pct}%</span>{gap && pct < 100 && <span className="text-muted-foreground">{gap} to go</span>}</div>
      <div className="h-1.5 rounded-full bg-secondary mt-1 overflow-hidden"><div className={`h-full ${tone}`} style={{ width: `${Math.min(100, pct)}%` }} /></div>
      {pct >= 100 && <Badge className="mt-1 text-[10px] px-1.5 py-0">achieved</Badge>}
    </div>
  );
}
